/**
 * AIActivityPanel — Case-level AI interaction timeline.
 *
 * Shows a chronological feed of every AI agent action taken on a case
 * so humans can see exactly what the AI did and why.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Brain, RefreshCw, ChevronDown, ChevronUp, AlertCircle, Clock, Cpu, Filter } from 'lucide-react';
import { getCaseAIActivity, getCaseAISummary, type AIActivityEntry, type AIActivitySummary } from '../services/aiActivityApi';

interface AIActivityPanelProps {
  reportId: string;
  className?: string;
}

const AGENT_COLORS: Record<string, React.CSSProperties> = {
  'Report Narrator': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  'Intelligence Agent': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  'Forensic Analyzer': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  'Settlement Calculator': { backgroundColor: 'transparent', color: 'var(--color-verified)' },
  'Future Closure Engine': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
  'Edge Case Narrator': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
};

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function ActivityEntry({ entry }: { entry: AIActivityEntry }) {
  const [expanded, setExpanded] = useState(false);
  const colorStyle = AGENT_COLORS[entry.agent_name] || { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
  const isError = entry.status === 'error';

  return (
    <div className="border rounded-lg p-3" style={isError ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' } : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span className="text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={colorStyle}>
            {entry.agent_name}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>{entry.action}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{formatTimestamp(entry.created_at)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {entry.duration_ms > 0 && (
            <span className="text-xs flex items-center gap-0.5" style={{ color: 'var(--color-text-dim)' }}>
              <Clock className="w-3 h-3" />
              {formatDuration(entry.duration_ms)}
            </span>
          )}
          {entry.tokens_used > 0 && (
            <span className="text-xs flex items-center gap-0.5" style={{ color: 'var(--color-text-dim)' }}>
              <Cpu className="w-3 h-3" />
              {entry.tokens_used.toLocaleString()}
            </span>
          )}
          {isError && <AlertCircle className="w-4 h-4" style={{ color: 'var(--color-brand)' }} />}
          <button onClick={() => setExpanded(!expanded)} className="p-0.5" style={{ color: 'var(--color-text-dim)' }}>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-3 space-y-2 text-xs border-t pt-2" style={{ borderColor: 'var(--color-border-sub)' }}>
          {entry.input_summary && (
            <div>
              <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Input:</span>
              <p className="mt-0.5 whitespace-pre-wrap" style={{ color: 'var(--color-text-muted)' }}>{entry.input_summary}</p>
            </div>
          )}
          {entry.output_summary && (
            <div>
              <span className="font-medium" style={{ color: 'var(--color-text-secondary)' }}>Output:</span>
              <p className="mt-0.5 whitespace-pre-wrap" style={{ color: 'var(--color-text-muted)' }}>{entry.output_summary}</p>
            </div>
          )}
          {isError && entry.error_message && (
            <div>
              <span className="font-medium" style={{ color: 'var(--color-needs-human-text)' }}>Error:</span>
              <p className="mt-0.5" style={{ color: 'var(--color-needs-human-text)' }}>{entry.error_message}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 pt-1" style={{ color: 'var(--color-text-dim)' }}>
            {entry.model && <span>Model: {entry.model}</span>}
            {entry.triggered_by && <span>By: {entry.triggered_by}</span>}
            {entry.cost_estimate > 0 && <span>Cost: ${entry.cost_estimate.toFixed(4)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AIActivityPanel({ reportId, className = '' }: AIActivityPanelProps) {
  const [entries, setEntries] = useState<AIActivityEntry[]>([]);
  const [summary, setSummary] = useState<AIActivitySummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [agentFilter, setAgentFilter] = useState<string>('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [activityData, summaryData] = await Promise.all([
        getCaseAIActivity(reportId, { agentName: agentFilter || undefined, limit: 100 }),
        getCaseAISummary(reportId),
      ]);
      setEntries(activityData);
      setSummary(summaryData);
    } catch (err) {
      console.error('Failed to fetch AI activity:', err);
    } finally {
      setLoading(false);
    }
  }, [reportId, agentFilter]);

  useEffect(() => {
    if (reportId) fetchData();
  }, [reportId, fetchData]);

  const agentNames = summary ? Object.keys(summary.agents) : [];

  return (
    <div className={`bg-white rounded-xl border shadow-sm ${className}`} style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'var(--color-border-sub)' }}>
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <Brain className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div>
            <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>AI Activity Trail</h3>
            {summary && summary.total_interactions > 0 && (
              <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                {summary.total_interactions} interactions across {agentNames.length} agents
                {summary.total_tokens > 0 && ` \u00B7 ${summary.total_tokens.toLocaleString()} tokens`}
              </p>
            )}
          </div>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="p-1.5 transition-colors rounded"
          style={{ color: 'var(--color-text-dim)' }}
          title="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Agent filter chips */}
      {agentNames.length > 1 && (
        <div className="flex items-center gap-2 px-4 py-2 border-b overflow-x-auto" style={{ borderColor: 'var(--color-border-faint)' }}>
          <Filter className="w-3.5 h-3.5 flex-shrink-0" style={{ color: 'var(--color-text-dim)' }} />
          <button
            onClick={() => setAgentFilter('')}
            className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${!agentFilter ? 'font-medium' : ''}`}
            style={!agentFilter ? { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' } : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }}
          >
            All
          </button>
          {agentNames.map((name) => (
            <button
              key={name}
              onClick={() => setAgentFilter(name === agentFilter ? '' : name)}
              className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${agentFilter === name ? 'font-medium' : ''}`}
              style={
                agentFilter === name
                  ? (AGENT_COLORS[name] || { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' })
                  : { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)' }
              }
            >
              {name} ({summary?.agents[name]?.count || 0})
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {loading && entries.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" style={{ color: 'var(--color-needs-human-text)' }} />
            <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Loading AI activity...</span>
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8">
            <Brain className="w-10 h-10 mx-auto mb-2" style={{ color: 'var(--color-text-ghost)' }} />
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No AI activity recorded for this case yet</p>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>Activity will appear here as AI agents analyze this case</p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {entries.map((entry) => (
              <ActivityEntry key={entry.activity_id} entry={entry} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
